import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, Search, CheckCircle2, ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { borderPrimary, primaryText, secondaryText, selectableName, semiBoldText, smallTextSize, tertiaryText } from '../../../styling/stylingHelper';
import { MultiSelectFilter } from '../../ui/MultiSelectFilter';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../ui/InventorySlideOver';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import {
  importInventoryDryRun,
  importInventoryCommit,
  ImportPlan,
  ImportDryRunSummary,
  ImportProductRow,
  ImportResolution,
  ImportCommitProduct,
  ImportCommitSummary,
  ImportRebuildPreviewRow,
} from '../../../services/inventory/inventoryService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  onCommitted: () => void;
}

type Step = 'upload' | 'review' | 'commit' | 'done';
// 'counts' = normal import (products into counted sections, optional count session).
// 'catalogue' = catch-all import: normal per-section import, then reconcile each
// section so every product sits in its sheet category and everything the file does
// not count there is parked in a "From Other Sections" category (still findable in
// the app's search). No counts.
// 'minimums' = only update each product's minimum stock ("Standaard voorraad") from the
// sheet, for files whose sections + counts were already imported; creates nothing else.
// 'rebuild' = rebuild each section's product list from the file: keep/add what the sheet
// counts there, remove everything else. Assignments only — saved counts are untouched,
// so history and current value don't move; it only shapes the NEXT count. No counts.
// 'suppliers' = write the sheet's "Leverancier" onto the CUSTOM ingredients its rows
// already resolve to (only where no supplier is set yet). Touches nothing else — no
// sections, categories, assignments, counts or minimums — so it skips the review step.
// 'complete' = top an existing structure up from a file that carries NO counts (an order
// list): every product the file has that is not assigned yet gets matched, linked and
// added to every section, and every row's minimum is re-stated. Purely additive — it
// removes nothing, moves nothing and writes no count. Which section a product really
// belongs in is a question only the next count can answer.
type ImportMode = 'counts' | 'minimums' | 'rebuild' | 'suppliers' | 'complete';

export default function ImportInventoryExcelModal({ isOpen, onClose, restaurantId, onCommitted }: Props) {
  const { t } = useTranslation();
  const { availableProducts, loadAvailableProducts, setImportSession, clearImportSession } = useInventoryStore();

  const [step, setStep] = useState<Step>('upload');
  const [mode, setMode] = useState<ImportMode>('counts');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [summary, setSummary] = useState<ImportDryRunSummary | null>(null);
  // Rebuild mode only: per-section keep/add/remove preview from the dry-run.
  const [rebuildPreview, setRebuildPreview] = useState<ImportRebuildPreviewRow[] | null>(null);

  // rowIndex -> resolution
  const [resolutions, setResolutions] = useState<Record<number, ImportResolution>>({});
  const [onlyWithStock, setOnlyWithStock] = useState(true);
  const [showImported, setShowImported] = useState(false);
  const [searchOpenFor, setSearchOpenFor] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSupplier, setSearchSupplier] = useState<string[]>([]);
  const [searchCategory, setSearchCategory] = useState<string[]>([]);
  // Which inline filter grid is expanded inside the search panel.
  const [searchFilterOpen, setSearchFilterOpen] = useState<'supplier' | 'category' | null>(null);

  // Reset every search filter — called when the search panel moves to another product.
  const resetSearchFilters = () => {
    setSearchTerm('');
    setSearchSupplier([]);
    setSearchCategory([]);
    setSearchFilterOpen(null);
  };

  // Open (or toggle closed) the order-search panel for a row, resetting filters
  // so the previous product's supplier/category/term never carry over.
  const toggleSearchFor = (rowIndex: number) => {
    resetSearchFilters();
    setSearchOpenFor((prev) => (prev === rowIndex ? null : rowIndex));
  };

  const [applyCounts, setApplyCounts] = useState(false);
  const [commitSummary, setCommitSummary] = useState<ImportCommitSummary | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    loadAvailableProducts(restaurantId);

    // Restore an in-progress wizard (e.g. closed by clicking outside after processing)
    // from the in-memory store rather than starting over.
    const saved = useInventoryStore.getState().importSession;
    if (saved && saved.restaurantId === restaurantId && saved.plan) {
      setStep(saved.step);
      setMode(saved.mode);
      setPlan(saved.plan);
      setSummary(saved.summary);
      setRebuildPreview(saved.rebuildPreview);
      setResolutions(saved.resolutions);
      setApplyCounts(saved.applyCounts);
      setCommitSummary(saved.commitSummary);
      setOnlyWithStock(saved.onlyWithStock);
      setShowImported(saved.showImported);
      // Transient UI (not persisted): the picked File can't be restored.
      setFile(null);
      setDragging(false);
      setBusy(false);
      setError(null);
      setSearchOpenFor(null);
      setSearchTerm('');
      setSearchSupplier([]);
      setSearchCategory([]);
      setSearchFilterOpen(null);
      return;
    }

    // Reset wizard for a fresh start.
    setStep('upload');
    setMode('counts');
    setFile(null);
    setDragging(false);
    setBusy(false);
    setError(null);
    setPlan(null);
    setSummary(null);
    setRebuildPreview(null);
    setResolutions({});
    setOnlyWithStock(true);
    setShowImported(false);
    setSearchOpenFor(null);
    setSearchTerm('');
    setSearchSupplier([]);
    setSearchCategory([]);
    setSearchFilterOpen(null);
    setApplyCounts(false);
    setCommitSummary(null);
  }, [isOpen, restaurantId, loadAvailableProducts]);

  // Snapshot in-progress wizard state to the store on close (preserves parsed plan +
  // review progress); clear it once committed or when nothing has been processed.
  const persistAndClose = () => {
    if (plan && step !== 'done') {
      setImportSession({ restaurantId, step, mode, plan, summary, rebuildPreview, resolutions, applyCounts, commitSummary, onlyWithStock, showImported });
    } else {
      clearImportSession();
    }
    onClose();
  };

  // Default resolution per product: auto→bind; everything uncertain (review AND
  // unmatched) → skip, so hitting Continue without reviewing only creates the
  // structure + sure matches. Customs are opted into explicitly (per-row or bulk).
  const initResolutions = (p: ImportPlan): Record<number, ImportResolution> => {
    const map: Record<number, ImportResolution> = {};
    for (const prod of p.products) {
      // Rows a previous run already saved default to skip — they're hidden and the
      // commit's dedup would skip them anyway.
      if (prod.alreadyImported) {
        map[prod.rowIndex] = { action: 'skip' };
      } else if (prod.match.status === 'auto' && prod.match.productKey) {
        map[prod.rowIndex] = { action: 'bind', productKeys: [prod.match.productKey] };
      } else {
        map[prod.rowIndex] = { action: 'skip' };
      }
    }
    return map;
  };

  const pickFile = (f: File | null | undefined) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      setError(t('inventory.import.invalidFile'));
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const runDryRun = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      // Minimums, suppliers and complete imports need zero-count rows kept: a row that
      // only carries a threshold — or a product that is simply at zero right now but whose
      // custom ingredient still needs its supplier — must come through, and an order list
      // is ALL zero-count rows. Counts and rebuild modes skip them: a row the file counts
      // nowhere belongs in no section (structure-only files keep their rows through sheet
      // membership instead).
      const includeZeroCount = mode === 'minimums' || mode === 'suppliers' || mode === 'complete';
      const res = await importInventoryDryRun(
        restaurantId,
        file,
        includeZeroCount,
        mode === 'rebuild',
        mode === 'suppliers',
        mode === 'complete'
      );
      setPlan(res.plan);
      setSummary(res.summary);
      setRebuildPreview(res.rebuildPreview ?? null);
      setResolutions(initResolutions(res.plan));
      // These modes target zero-count rows, so don't hide them behind the
      // "only with stock" filter.
      if (includeZeroCount || res.plan.products.some(p => p.memberSections?.length)) setOnlyWithStock(false);
      // Suppliers mode makes no per-row decision — the server resolves each row to the
      // custom ingredient it is already assigned to, so there is nothing to review.
      setStep(mode === 'suppliers' ? 'commit' : 'review');
    } catch (err: any) {
      setError(err?.message || 'Dry-run failed');
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!plan) return;
    setBusy(true);
    setError(null);
    try {
      const products: ImportCommitProduct[] = plan.products.map((p) => ({
        rowIndex: p.rowIndex,
        name: p.name,
        category: p.category,
        counts: p.counts,
        excelNetto: p.excelNetto,
        minimum: p.minimum,
        supplier: p.supplier,
        memberSections: p.memberSections,
        resolution: resolutions[p.rowIndex] ?? { action: 'skip' },
      }));
      const minimums = mode === 'minimums';
      const rebuild = mode === 'rebuild';
      const suppliers = mode === 'suppliers';
      const complete = mode === 'complete';
      // Minimums mode only upserts thresholds server-side (no counts, no section
      // assignment). Rebuild reshapes the section product lists server-side and
      // never writes counts. Complete only adds products + minimums. Suppliers mode
      // writes custom_ingredients only.
      // Structure-only plans (no counts in the file) can never book a count session either.
      const res = await importInventoryCommit(
        restaurantId,
        plan.categories,
        products,
        minimums || rebuild || suppliers || complete || plan.structureOnly ? false : applyCounts,
        plan.sections,
        plan.fileDate ?? null,
        minimums,
        rebuild,
        suppliers,
        complete
      );
      setCommitSummary(res);
      setStep('done');
      clearImportSession(); // committed — drop the saved in-progress snapshot
      onCommitted();
    } catch (err: any) {
      // The rebuild is refused while a count is running on the group — deleting
      // assignments mid-session would drop products a teammate already counted.
      setError(err?.message === 'active_session' ? t('inventory.import.rebuildActiveSession') : (err?.message || 'Commit failed'));
    } finally {
      setBusy(false);
    }
  };

  // Rows that need human attention (not auto-matched).
  const reviewRows: ImportProductRow[] = useMemo(() => {
    if (!plan) return [];
    return plan.products
      .filter((p) => p.match.status !== 'auto')
      .filter((p) => (showImported ? true : !p.alreadyImported))
      .filter((p) => (onlyWithStock ? p.hasCount : true))
      .sort((a, b) => Number(b.hasCount) - Number(a.hasCount) || b.totalCount - a.totalCount);
  }, [plan, onlyWithStock, showImported]);

  // Distinct supplier + category options for the search filters.
  const supplierOptions = useMemo(
    () => Array.from(new Set(availableProducts.map((a) => a.platform).filter(Boolean))).sort(),
    [availableProducts]
  );
  const categoryOptions = useMemo(
    () => Array.from(new Set(availableProducts.map((a) => a.category).filter((c): c is string => !!c))).sort(),
    [availableProducts]
  );

  const filteredAvailable = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const list = availableProducts.filter((a) => {
      if (term && !a.productName.toLowerCase().includes(term)) return false;
      if (searchSupplier.length > 0 && !searchSupplier.includes(a.platform)) return false;
      if (searchCategory.length > 0 && (!a.category || !searchCategory.includes(a.category))) return false;
      return true;
    });
    return list.slice(0, 20);
  }, [availableProducts, searchTerm, searchSupplier, searchCategory]);

  const setResolution = (rowIndex: number, r: ImportResolution) =>
    setResolutions((prev) => ({ ...prev, [rowIndex]: r }));

  // productKey -> display metadata (name, supplier, last order date, 60d spend),
  // merged from order-history search results and the plan's AI candidate shortlists.
  const metaByKey = useMemo(() => {
    const m = new Map<string, { name: string; supplier?: string; lastOrderDate?: string; last60Qty?: number; last60Unit?: string; unitPrice?: number; unitPriceUnit?: string; productPrice?: number }>();
    for (const a of availableProducts) {
      m.set(a.productKey, { name: a.productName, supplier: a.platform, lastOrderDate: a.latestPurchaseDate, last60Qty: a.last60Qty, last60Unit: a.last60Unit, unitPrice: a.unitPrice, unitPriceUnit: a.unitPriceUnit, productPrice: a.productPrice });
    }
    for (const p of plan?.products ?? []) {
      for (const s of p.match.suggestions ?? []) {
        if (!m.has(s.productKey)) m.set(s.productKey, { name: s.productName, supplier: s.supplier, lastOrderDate: s.lastOrderDate, last60Qty: s.last60Qty, last60Unit: s.last60Unit, unitPrice: s.unitPrice, unitPriceUnit: s.unitPriceUnit, productPrice: s.productPrice });
      }
    }
    return m;
  }, [availableProducts, plan]);

  const fmtMeta = (key: string): string => {
    const meta = metaByKey.get(key);
    if (!meta) return '';
    const bits: string[] = [];
    if (meta.supplier) bits.push(meta.supplier);
    // Buy price per product — the one that matches the Excel keyed price when linking.
    if (typeof meta.productPrice === 'number' && meta.productPrice > 0) {
      bits.push(t('inventory.import.buyPrice', { value: meta.productPrice.toFixed(2) }));
    }
    if (typeof meta.unitPrice === 'number') {
      bits.push(t('inventory.import.unitPrice', { value: meta.unitPrice.toFixed(2), unit: meta.unitPriceUnit ?? 'pcs' }));
    }
    if (meta.lastOrderDate) bits.push(t('inventory.import.lastOrdered', { date: new Date(meta.lastOrderDate).toLocaleDateString() }));
    if (typeof meta.last60Qty === 'number') {
      const v = Math.round(meta.last60Qty * 10) / 10;
      bits.push(t('inventory.import.qty60', { value: v, unit: meta.last60Unit ?? 'pcs' }));
    }
    return bits.join(' · ');
  };

  const getBinds = (rowIndex: number): string[] => {
    const r = resolutions[rowIndex];
    return r?.action === 'bind' ? r.productKeys : [];
  };

  const addBind = (rowIndex: number, productKey: string) => {
    const current = getBinds(rowIndex);
    if (current.includes(productKey)) return;
    setResolution(rowIndex, { action: 'bind', productKeys: [...current, productKey] });
  };

  const removeBind = (rowIndex: number, productKey: string) => {
    const next = getBinds(rowIndex).filter((k) => k !== productKey);
    setResolution(rowIndex, next.length > 0 ? { action: 'bind', productKeys: next } : { action: 'skip' });
  };

  const bulkSet = (action: 'createCustom' | 'skip') => {
    if (!plan) return;
    setResolutions((prev) => {
      const next = { ...prev };
      for (const p of plan.products) {
        if (p.match.status === 'auto') continue;
        // Rows a previous run already saved are HIDDEN from the review list, so a bulk
        // action must never touch them: "create custom" on an invisible row duplicates a
        // product that is already assigned (and already linked) as a fresh, unlinked custom.
        if (p.alreadyImported) continue;
        next[p.rowIndex] = action === 'createCustom' ? { action: 'createCustom', price: p.excelNetto ?? 0 } : { action: 'skip' };
      }
      return next;
    });
  };

  const countResolved = (action: ImportResolution['action']) =>
    Object.values(resolutions).filter((r) => r.action === action).length;

  // Review progress: how many of the visible rows the user has handled (bound to an
  // order product or marked custom). Skipped/untouched rows don't count as done.
  const reviewTotal = reviewRows.length;
  const reviewDone = reviewRows.filter((p) => {
    const a = resolutions[p.rowIndex]?.action;
    return a === 'bind' || a === 'createCustom';
  }).length;
  const reviewPct = reviewTotal > 0 ? Math.round((reviewDone / reviewTotal) * 100) : 0;

  // Footer buttons per step — rendered in the InventorySlideOver footer slot.
  const getFooter = () => {
    if (step === 'upload') {
      return (
        <>
          <button type="button" onClick={persistAndClose} className={slideOverCancelClass}>
            {t('inventory.cancel')}
          </button>
          <button
            type="button"
            onClick={runDryRun}
            disabled={!file || busy}
            className={slideOverPrimaryClass}
          >
            {busy ? t('inventory.import.analyzing') : t('inventory.import.analyze')}
          </button>
        </>
      );
    }
    if (step === 'review') {
      return (
        <>
          <button onClick={() => setStep('upload')} className={slideOverCancelClass}>
            {t('inventory.import.back')}
          </button>
          <button onClick={() => setStep('commit')} className={slideOverPrimaryClass}>
            {t('inventory.import.continue')}
          </button>
        </>
      );
    }
    if (step === 'commit') {
      return (
        <>
          {/* Suppliers mode has no review step — Back returns to the upload screen. */}
          <button onClick={() => setStep(mode === 'suppliers' ? 'upload' : 'review')} disabled={busy} className={slideOverCancelClass}>
            {t('inventory.import.back')}
          </button>
          <button onClick={commit} disabled={busy} className={slideOverPrimaryClass}>
            {busy ? t('inventory.import.creating') : t('inventory.import.create')}
          </button>
        </>
      );
    }
    if (step === 'done') {
      return (
        <button onClick={persistAndClose} className={slideOverPrimaryClass}>
          {t('inventory.import.close')}
        </button>
      );
    }
    return null;
  };

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={persistAndClose}
      title={t('inventory.import.title')}
      description={t('inventory.import.subtitle')}
      footer={getFooter()}
    >
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* STEP 1 — Upload */}
          {step === 'upload' && (
            <div className="pb-4">
              {/* Mode picker */}
              <div className="space-y-3 mb-5">
                <label className={cn('flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors', borderPrimary, mode === 'counts' && 'bg-darkGreenColor/5 border-darkGreenColor/30 dark:bg-darkGreenColor-dark/5 dark:border-darkGreenColor-dark/30')}>
                  <input type="radio" name="importMode" checked={mode === 'counts'} onChange={() => setMode('counts')} className="w-4 h-4 mt-0.5 accent-[#0E8965]" />
                  <span className="flex-1">
                    <span className={cn('text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50 block')}>{t('inventory.import.modeCounts')}</span>
                    <span className={cn('text-[12px] sm:text-[14px] leading-4 text-[#667085] dark:text-zinc-400 block mt-1')}>{t('inventory.import.modeCountsDesc')}</span>
                  </span>
                </label>
                <label className={cn('flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors', borderPrimary, mode === 'minimums' && 'bg-darkGreenColor/5 border-darkGreenColor/30 dark:bg-darkGreenColor-dark/5 dark:border-darkGreenColor-dark/30')}>
                  <input type="radio" name="importMode" checked={mode === 'minimums'} onChange={() => setMode('minimums')} className="w-4 h-4 mt-0.5 accent-[#0E8965]" />
                  <span className="flex-1">
                    <span className={cn('text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50 block')}>{t('inventory.import.modeMinimums')}</span>
                    <span className={cn('text-[12px] sm:text-[14px] leading-4 text-[#667085] dark:text-zinc-400 block mt-1')}>{t('inventory.import.modeMinimumsDesc')}</span>
                  </span>
                </label>
                <label className={cn('flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors', borderPrimary, mode === 'rebuild' && 'bg-darkGreenColor/5 border-darkGreenColor/30 dark:bg-darkGreenColor-dark/5 dark:border-darkGreenColor-dark/30')}>
                  <input type="radio" name="importMode" checked={mode === 'rebuild'} onChange={() => setMode('rebuild')} className="w-4 h-4 mt-0.5 accent-[#0E8965]" />
                  <span className="flex-1">
                    <span className={cn('text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50 block')}>{t('inventory.import.modeRebuild')}</span>
                    <span className={cn('text-[12px] sm:text-[14px] leading-4 text-[#667085] dark:text-zinc-400 block mt-1')}>{t('inventory.import.modeRebuildDesc')}</span>
                  </span>
                </label>
                <label className={cn('flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors', borderPrimary, mode === 'complete' && 'bg-darkGreenColor/5 border-darkGreenColor/30 dark:bg-darkGreenColor-dark/5 dark:border-darkGreenColor-dark/30')}>
                  <input type="radio" name="importMode" checked={mode === 'complete'} onChange={() => setMode('complete')} className="w-4 h-4 mt-0.5 accent-[#0E8965]" />
                  <span className="flex-1">
                    <span className={cn('text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50 block')}>{t('inventory.import.modeComplete')}</span>
                    <span className={cn('text-[12px] sm:text-[14px] leading-4 text-[#667085] dark:text-zinc-400 block mt-1')}>{t('inventory.import.modeCompleteDesc')}</span>
                  </span>
                </label>
                <label className={cn('flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors', borderPrimary, mode === 'suppliers' && 'bg-darkGreenColor/5 border-darkGreenColor/30 dark:bg-darkGreenColor-dark/5 dark:border-darkGreenColor-dark/30')}>
                  <input type="radio" name="importMode" checked={mode === 'suppliers'} onChange={() => setMode('suppliers')} className="w-4 h-4 mt-0.5 accent-[#0E8965]" />
                  <span className="flex-1">
                    <span className={cn('text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50 block')}>{t('inventory.import.modeSuppliers')}</span>
                    <span className={cn('text-[12px] sm:text-[14px] leading-4 text-[#667085] dark:text-zinc-400 block mt-1')}>{t('inventory.import.modeSuppliersDesc')}</span>
                  </span>
                </label>
              </div>
              <label
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn(
                  'flex flex-col items-center justify-center gap-3 p-10 rounded-2xl border-2 border-dashed cursor-pointer transition-colors',
                  dragging ? 'border-darkGreenColor bg-darkGreenColor/5 dark:border-darkGreenColor-dark dark:bg-darkGreenColor-dark/5' : borderPrimary,
                  'hover:bg-gray-50 dark:hover:bg-gray-800'
                )}
              >
                <Upload className="w-8 h-8 text-gray-400 dark:text-textQuaternary-dark" />
                <span className={cn(smallTextSize, secondaryText, 'text-center')}>
                  {file ? file.name : t('inventory.import.dropOrChoose')}
                </span>
                <input
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />
              </label>
            </div>
          )}

          {/* STEP 2 — Review */}
          {step === 'review' && plan && summary && (
            <div>
              {/* Summary chips */}
              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  { label: t('inventory.import.summarySections'), value: summary.sections },
                  { label: t('inventory.import.summaryCategories'), value: summary.categories },
                  { label: t('inventory.import.summaryWithStock'), value: summary.productsWithCount },
                  { label: t('inventory.import.summaryAutoMatched'), value: summary.autoMatched },
                  { label: t('inventory.import.summaryToReview'), value: summary.toReview },
                  { label: t('inventory.import.summaryUnmatched'), value: summary.unmatched },
                  { label: t('inventory.import.summaryAlreadyImported'), value: summary.alreadyImported },
                  ...(mode === 'minimums' ? [{ label: t('inventory.import.summaryWithMinimum'), value: summary.withMinimum }] : []),
                ].map((chip, i) => (
                  <span key={i} className={cn(smallTextSize, 'px-3 py-1.5 rounded-lg border', borderPrimary, secondaryText)}>
                    {chip.label}: <span className={cn(semiBoldText, primaryText)}>{chip.value}</span>
                  </span>
                ))}
              </div>

              {/* Review progress */}
              {reviewTotal > 0 && (
                <div className="sticky top-0 z-10 -mx-6 px-6 py-3 mb-4 bg-card dark:bg-card-dark border-b border-zinc-200 dark:border-white/20">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={cn(smallTextSize, semiBoldText, primaryText)}>
                      {t('inventory.import.reviewProgress', { done: reviewDone, total: reviewTotal })}
                    </span>
                    <span className={cn('text-[11px]', tertiaryText)}>
                      {reviewDone === reviewTotal ? t('inventory.import.reviewAllDone') : `${reviewPct}%`}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-darkGreenColor dark:bg-darkGreenColor-dark transition-all duration-300"
                      style={{ width: `${reviewPct}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Controls */}
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-4">
                  <label className={cn(smallTextSize, secondaryText, 'flex items-center gap-2')}>
                    <input type="checkbox" checked={onlyWithStock} onChange={(e) => setOnlyWithStock(e.target.checked)} />
                    {t('inventory.import.onlyWithStock')}
                  </label>
                  {summary.alreadyImported > 0 && (
                    <label className={cn(smallTextSize, secondaryText, 'flex items-center gap-2')}>
                      <input type="checkbox" checked={showImported} onChange={(e) => setShowImported(e.target.checked)} />
                      {t('inventory.import.showAlreadyImported')}
                    </label>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn('text-[11px]', tertiaryText)}>{t('inventory.import.bulkDefault')}:</span>
                  <button onClick={() => bulkSet('createCustom')} className={cn('text-[11px] px-2 py-1 rounded-lg border', borderPrimary, secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')}>
                    {t('inventory.import.createCustom')}
                  </button>
                  <button onClick={() => bulkSet('skip')} className={cn('text-[11px] px-2 py-1 rounded-lg border', borderPrimary, secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')}>
                    {t('inventory.import.skip')}
                  </button>
                </div>
              </div>

              {/* Review list */}
              <div className="space-y-2">
                {reviewRows.length === 0 ? (
                  <p className={cn(smallTextSize, tertiaryText, 'text-center py-6')}>{t('inventory.import.nothingToReview')}</p>
                ) : (
                  reviewRows.map((p) => {
                    const r = resolutions[p.rowIndex] ?? { action: 'skip' };
                    const binds = r.action === 'bind' ? r.productKeys : [];
                    // Candidate chips not already bound.
                    const candChips = (p.match.suggestions ?? []).filter((s) => !binds.includes(s.productKey));
                    return (
                      <div key={p.rowIndex} className={cn('p-3 rounded-xl border', borderPrimary)}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className={cn(smallTextSize, semiBoldText, primaryText, selectableName)}>{p.name}</p>
                            <p className={cn('text-[11px]', tertiaryText)}>
                              {p.category}
                              {Object.entries(p.counts).filter(([, v]) => v > 0).length > 0 &&
                                ` · ${Object.entries(p.counts).filter(([, v]) => v > 0).map(([s, v]) => `${s} ${v}`).join(' / ')}`}
                              {mode === 'minimums' && typeof p.minimum === 'number' && (
                                <span className="ml-1 text-darkGreenColor dark:text-darkGreenColor-dark">
                                  · {t('inventory.import.rowMinimum', { value: p.minimum })}
                                </span>
                              )}
                              {p.match.method === 'ai' && typeof p.match.confidence === 'number' && (
                                <span className="ml-1 text-amber-600 dark:text-amber-400">
                                  · {t('inventory.import.aiConfidence', { pct: Math.round(p.match.confidence * 100) })}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {typeof p.excelNetto === 'number' && p.excelNetto > 0 && (
                              <span className={cn('text-[11px] px-2 py-1 rounded-lg border font-semibold', borderPrimary, primaryText, 'bg-amber-50 dark:bg-amber-900/20')}>
                                {t('inventory.import.filePrice', { value: p.excelNetto.toFixed(2) })}
                              </span>
                            )}
                            <button
                              onClick={() => toggleSearchFor(p.rowIndex)}
                              className={cn('text-[11px] px-2 py-1 rounded-lg border flex items-center gap-1', borderPrimary, searchOpenFor === p.rowIndex ? 'bg-darkGreenColor/10 text-darkGreenColor dark:bg-darkGreenColor-dark/10 dark:text-darkGreenColor-dark' : secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')}
                            >
                              <Search className="w-3 h-3" /> {t('inventory.import.bind')}
                            </button>
                            <button
                              onClick={() => setResolution(p.rowIndex, { action: 'createCustom', price: p.excelNetto ?? 0 })}
                              className={cn('text-[11px] px-2 py-1 rounded-lg border', borderPrimary, r.action === 'createCustom' ? 'bg-darkGreenColor/10 text-darkGreenColor dark:bg-darkGreenColor-dark/10 dark:text-darkGreenColor-dark' : secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')}
                            >
                              {t('inventory.import.createCustom')}
                            </button>
                            <button
                              onClick={() => setResolution(p.rowIndex, { action: 'skip' })}
                              className={cn('text-[11px] px-2 py-1 rounded-lg border', borderPrimary, r.action === 'skip' ? 'bg-gray-100 dark:bg-gray-700' : secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')}
                            >
                              {t('inventory.import.skip')}
                            </button>
                          </div>
                        </div>

                        {/* Bound products (chips). First one carries the count. */}
                        {binds.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {binds.map((key, idx) => (
                              <div key={key} className="flex items-center justify-between gap-2 p-1.5 pl-2 rounded-lg bg-green-50 dark:bg-green-900/20">
                                <div className="min-w-0">
                                  <p className={cn('text-[11px] text-green-700 dark:text-green-300 flex flex-wrap items-start gap-1')}>
                                    <CheckCircle2 className="w-3 h-3 shrink-0 mt-px" />
                                    <span className={selectableName}>{metaByKey.get(key)?.name ?? key}</span>
                                    {idx === 0 && binds.length > 1 && (
                                      <span className="text-[10px] px-1 rounded bg-green-200/60 dark:bg-green-800/60 shrink-0">{t('inventory.import.countTarget')}</span>
                                    )}
                                  </p>
                                  {fmtMeta(key) && <p className="text-[10px] text-green-600/80 dark:text-green-400/70 truncate pl-4">{fmtMeta(key)}</p>}
                                </div>
                                <button onClick={() => removeBind(p.rowIndex, key)} className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-800/40 shrink-0">
                                  <X className="w-3 h-3 text-green-700 dark:text-green-300" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Candidate quick-picks (AI-pruned). Click to ADD. */}
                        {candChips.length > 0 && r.action !== 'createCustom' && (
                          <div className="mt-2 space-y-1">
                            {candChips.map((s) => (
                              <button
                                key={s.productKey}
                                onClick={() => addBind(p.rowIndex, s.productKey)}
                                className={cn('block w-full text-left px-2 py-1 rounded-lg border', borderPrimary, 'hover:bg-blue-50 dark:hover:bg-blue-900/20')}
                              >
                                <span className="text-[11px] text-blue-600 dark:text-blue-400">+ {s.productName}</span>
                                {fmtMeta(s.productKey) && <span className={cn('block text-[10px]', tertiaryText)}>{fmtMeta(s.productKey)}</span>}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Custom price editor */}
                        {r.action === 'createCustom' && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className={cn('text-[11px]', tertiaryText)}>{t('inventory.import.customPrice')}</span>
                            <input
                              type="number"
                              step="0.01"
                              value={r.price}
                              onChange={(e) => setResolution(p.rowIndex, { action: 'createCustom', price: Number(e.target.value) || 0 })}
                              className={cn('w-24 px-2 py-1 rounded-lg border text-sm', borderPrimary, 'bg-white dark:bg-zinc-900')}
                            />
                          </div>
                        )}

                        {/* Order-history search — click a result to ADD it to the binds. */}
                        {searchOpenFor === p.rowIndex && (
                          <div className="mt-3 p-3 rounded-xl border bg-gray-50/60 dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700">
                            <div className="flex flex-col sm:flex-row gap-2">
                              <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-textQuaternary-dark" />
                                <input
                                  autoFocus
                                  type="text"
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  placeholder={t('inventory.import.searchPlaceholder') as string}
                                  className={cn('w-full pl-9 pr-3 py-2 rounded-xl border text-sm', borderPrimary, 'bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500')}
                                />
                              </div>
                              <div className="flex gap-2 shrink-0">
                                {([
                                  { key: 'supplier' as const, label: t('inventory.filterPlatform'), count: searchSupplier.length },
                                  { key: 'category' as const, label: t('inventory.filterCategory'), count: searchCategory.length },
                                ]).map((f) => (
                                  <button
                                    key={f.key}
                                    type="button"
                                    onClick={() => setSearchFilterOpen((o) => (o === f.key ? null : f.key))}
                                    className={cn(
                                      'px-3 py-2 rounded-xl border bg-white dark:bg-zinc-900',
                                      borderPrimary, smallTextSize, semiBoldText, secondaryText,
                                      'flex items-center gap-1.5 hover:bg-gray-50 dark:hover:bg-gray-800',
                                      searchFilterOpen === f.key && 'ring-2 ring-green-500/40 dark:ring-green-500/40'
                                    )}
                                  >
                                    <span>{f.label}</span>
                                    {f.count > 0 && (
                                      <span className="bg-darkGreenColor text-white text-xs rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center dark:bg-darkGreenColor-dark dark:text-white">
                                        {f.count}
                                      </span>
                                    )}
                                    <ChevronDown className={cn('w-4 h-4 transition-transform', searchFilterOpen === f.key && 'rotate-180')} />
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Inline filter grid — rendered in-flow so it's never clipped by the modal scroll. */}
                            {searchFilterOpen && (
                              <div className={cn('mt-2 p-3 rounded-xl border bg-white dark:bg-zinc-900', borderPrimary)}>
                                {searchFilterOpen === 'supplier' ? (
                                  <MultiSelectFilter
                                    label={t('inventory.filterPlatform')}
                                    selectedValues={searchSupplier}
                                    onValuesChange={setSearchSupplier}
                                    availableOptions={supplierOptions}
                                    valueType="default"
                                    maxHeight="max-h-[140px]"
                                  />
                                ) : (
                                  <MultiSelectFilter
                                    label={t('inventory.filterCategory')}
                                    selectedValues={searchCategory}
                                    onValuesChange={setSearchCategory}
                                    availableOptions={categoryOptions}
                                    valueType="category"
                                    maxHeight="max-h-[140px]"
                                  />
                                )}
                              </div>
                            )}

                            <div className="mt-2 max-h-44 overflow-y-auto">
                              {filteredAvailable.map((a) => (
                                <button
                                  key={a.productKey}
                                  onClick={() => addBind(p.rowIndex, a.productKey)}
                                  className={cn('block w-full text-left px-3 py-2 mb-1 rounded-lg bg-white dark:bg-zinc-900 border border-transparent hover:border-darkGreenColor/50 dark:hover:border-darkGreenColor-dark/50 transition-colors')}
                                >
                                  <span className={cn('text-sm', secondaryText)}>{a.productName}</span>
                                  {fmtMeta(a.productKey) && <span className={cn('block text-[10px]', tertiaryText)}>{fmtMeta(a.productKey)}</span>}
                                </button>
                              ))}
                              {filteredAvailable.length === 0 && (
                                <p className={cn('text-[11px] px-3 py-3 text-center', tertiaryText)}>{t('inventory.import.noResults')}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* STEP 3 — Commit */}
          {step === 'commit' && plan && (
            <div className="py-2">
              {mode !== 'suppliers' && (
                <p className={cn(smallTextSize, secondaryText, 'mb-4')}>
                  {t('inventory.import.willCreate', {
                    bind: countResolved('bind'),
                    custom: countResolved('createCustom'),
                    skip: countResolved('skip'),
                  })}
                </p>
              )}

              {mode === 'suppliers' ? (
                <div className={cn('mb-6 p-3 rounded-xl border', borderPrimary, 'bg-darkGreenColor/5 dark:bg-darkGreenColor-dark/5')}>
                  <span className={cn(smallTextSize, semiBoldText, primaryText, 'block')}>
                    {t('inventory.import.suppliersCommit', { rows: summary?.withSupplier ?? 0 })}
                  </span>
                  <span className={cn('text-[11px]', tertiaryText)}>{t('inventory.import.suppliersCommitDesc')}</span>
                </div>
              ) : mode === 'minimums' ? (
                <div className={cn('mb-6 p-3 rounded-xl border', borderPrimary, 'bg-darkGreenColor/5 dark:bg-darkGreenColor-dark/5')}>
                  <span className={cn(smallTextSize, semiBoldText, primaryText, 'block')}>{t('inventory.import.minimumsCommit')}</span>
                  <span className={cn('text-[11px]', tertiaryText)}>{t('inventory.import.minimumsCommitDesc')}</span>
                </div>
              ) : mode === 'complete' ? (
                <div className={cn('mb-6 p-3 rounded-xl border', borderPrimary, 'bg-darkGreenColor/5 dark:bg-darkGreenColor-dark/5')}>
                  <span className={cn(smallTextSize, semiBoldText, primaryText, 'block')}>
                    {t('inventory.import.completeCommit', {
                      bind: countResolved('bind'),
                      custom: countResolved('createCustom'),
                      minimums: summary?.withMinimum ?? 0,
                    })}
                  </span>
                  <span className={cn('text-[11px]', tertiaryText)}>{t('inventory.import.completeCommitDesc')}</span>
                </div>
              ) : mode === 'rebuild' ? (
                <div className={cn('mb-6 p-3 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20')}>
                  <span className={cn(smallTextSize, semiBoldText, primaryText, 'block')}>{t('inventory.import.rebuildCommit')}</span>
                  <span className={cn('text-[11px]', tertiaryText)}>{t('inventory.import.rebuildCommitDesc')}</span>
                  {rebuildPreview && rebuildPreview.length > 0 && (
                    <table className="w-full mt-3 text-[11px]">
                      <thead>
                        <tr className={tertiaryText}>
                          <th className="text-left font-normal pb-1">{t('inventory.import.rebuildSection')}</th>
                          <th className="text-right font-normal pb-1">{t('inventory.import.rebuildKeep')}</th>
                          <th className="text-right font-normal pb-1">{t('inventory.import.rebuildAdd')}</th>
                          <th className="text-right font-normal pb-1">{t('inventory.import.rebuildRemove')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rebuildPreview.map((r) => (
                          <tr key={r.section}>
                            <td className={cn('text-left py-0.5', secondaryText)}>{r.section}</td>
                            <td className={cn('text-right py-0.5', secondaryText)}>{r.keep}</td>
                            <td className={cn('text-right py-0.5', secondaryText)}>+{r.add}</td>
                            <td className={cn('text-right py-0.5 font-semibold text-amber-700 dark:text-amber-400')}>-{r.remove}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : plan.structureOnly ? (
                // The file carries no counts (membership by sheet) — there is nothing
                // to apply, so the structure-only choice is fixed instead of offered.
                <div className={cn('mb-6 p-3 rounded-xl border', borderPrimary, 'bg-darkGreenColor/5 dark:bg-darkGreenColor-dark/5')}>
                  <span className={cn(smallTextSize, semiBoldText, primaryText, 'block')}>{t('inventory.import.structureOnly')}</span>
                  <span className={cn('text-[11px]', tertiaryText)}>{t('inventory.import.structureOnlyFileDesc')}</span>
                </div>
              ) : (
                <div className="space-y-2 mb-6">
                  <label className={cn('flex items-start gap-3 p-3 rounded-xl border cursor-pointer', borderPrimary, !applyCounts && 'bg-darkGreenColor/5 dark:bg-darkGreenColor-dark/5')}>
                    <input type="radio" name="applyCounts" checked={!applyCounts} onChange={() => setApplyCounts(false)} className="mt-1" />
                    <span>
                      <span className={cn(smallTextSize, semiBoldText, primaryText, 'block')}>{t('inventory.import.structureOnly')}</span>
                      <span className={cn('text-[11px]', tertiaryText)}>{t('inventory.import.structureOnlyDesc')}</span>
                    </span>
                  </label>
                  <label className={cn('flex items-start gap-3 p-3 rounded-xl border cursor-pointer', borderPrimary, applyCounts && 'bg-darkGreenColor/5 dark:bg-darkGreenColor-dark/5')}>
                    <input type="radio" name="applyCounts" checked={applyCounts} onChange={() => setApplyCounts(true)} className="mt-1" />
                    <span>
                      <span className={cn(smallTextSize, semiBoldText, primaryText, 'block')}>{t('inventory.import.structureAndCounts')}</span>
                      <span className={cn('text-[11px]', tertiaryText)}>{t('inventory.import.structureAndCountsDesc')}</span>
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* STEP 4 — Done */}
          {step === 'done' && commitSummary && (
            <div className="py-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 dark:text-textGreen-dark mx-auto mb-3" />
              <p className={cn(semiBoldText, primaryText, 'mb-4')}>{t('inventory.import.doneTitle')}</p>
              <div className="inline-block text-left space-y-1">
                {mode === 'suppliers' ? (
                  <>
                    <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultSuppliers')}: {commitSummary.customSuppliersBackfilled ?? 0}</p>
                    <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultSkipped')}: {commitSummary.skipped}</p>
                  </>
                ) : mode === 'minimums' ? (
                  <>
                    <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultMinimums')}: {commitSummary.minimumsApplied ?? 0}</p>
                    <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultSkipped')}: {commitSummary.skipped}</p>
                  </>
                ) : (
                  <>
                    <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultSections')}: {commitSummary.sectionsCreated}</p>
                    <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultCategories')}: {commitSummary.categoriesCreated}</p>
                    <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultAssignments')}: {commitSummary.assignmentsCreated}</p>
                    <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultCustom')}: {commitSummary.customIngredientsCreated}</p>
                    {mode === 'rebuild' ? (
                      <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultRemoved')}: {commitSummary.rebuildRemoved ?? 0}</p>
                    ) : mode === 'complete' ? (
                      <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultMinimums')}: {commitSummary.minimumsApplied ?? 0}</p>
                    ) : (
                      <p className={cn(smallTextSize, secondaryText)}>{t('inventory.import.resultCounts')}: {commitSummary.countsApplied}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
    </InventorySlideOver>
  );
}
